import { Building2, CalendarDays, ChevronRight, Download, FolderOpenDot, Handshake, Home, ListChecks, LogIn, LogOut, Medal, Menu, Newspaper, Shield, Swords, UserCog, UserPlus, X } from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { useAuth } from "@/context/AuthContext";
import InstallModal from "@/components/InstallModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fight-bracket-pro/artifacts/bcn58mfl_WBCGermany.png";

// Hauptnavigation im Burger für eingeloggte Nutzer.
const loggedInNav = [
  { to: "/events", label: "Events", icon: CalendarDays, end: false },
  { to: "/calendar", label: "Kalender", icon: CalendarDays, end: false },
  { to: "/news", label: "News", icon: Newspaper, end: false },
  { to: "/projekte", label: "Projekte", icon: FolderOpenDot, end: false },
  { to: "/sponsoren", label: "Sponsoren", icon: Handshake, end: false },
  { to: "/my-fights", label: "Meine Kämpfe", icon: Swords, end: false },
  { to: "/rankings", label: "Rangliste", icon: Medal, end: false },
  { to: "/gyms", label: "WBC Gyms", icon: Building2, end: false },
];

// Hauptnavigation im Burger für Gäste (vor dem Login).
const publicNav = [
  { to: "/", label: "Startseite", icon: Home, end: true },
  { to: "/news", label: "News", icon: Newspaper, end: false },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, end: false },
  { to: "/projekte", label: "Projekte", icon: FolderOpenDot, end: false },
  { to: "/sponsoren", label: "Sponsoren", icon: Handshake, end: false },
  { to: "/gyms", label: "WBC Gyms", icon: Building2, end: false },
  { to: "/rangliste", label: "Rangliste", icon: Medal, end: false },
];

// Admin-Pflegebereiche in der unteren Leiste (als Sheet-Inhalt).
const adminItems = [
  { to: "/calendar/admin", label: "Kalender pflegen", icon: CalendarDays, superOnly: true },
  { to: "/news-admin", label: "News pflegen", icon: Newspaper },
  { to: "/projekte-admin", label: "Projekte pflegen", icon: FolderOpenDot },
  { to: "/sponsoren-admin", label: "Sponsoren pflegen", icon: Handshake },
  { to: "/settings/rankings", label: "Rangliste pflegen", icon: Medal },
  { to: "/gyms-admin", label: "WBC Gyms pflegen", icon: Building2 },
  { to: "/settings/weight-classes", label: "Gewichtsklassen", icon: ListChecks },
  { to: "/settings/users", label: "Benutzer & Rollen", icon: UserCog, superOnly: true },
];

/**
 * Zentrale mobile Navigation — wird von AppShell, PublicShell UND den
 * Login-/Registrierungsseiten eingebunden, damit die Nav auf JEDER Seite
 * identisch aussieht und funktioniert. CSS-Regeln blenden alle Elemente auf
 * Desktop (>820px) aus.
 */
export default function MobileNavigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [adminSheetOpen, setAdminSheetOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const canManageEvents = ["super_admin", "event_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const visibleAdminItems = adminItems.filter((i) => !i.superOnly || isSuper);
  const navItems = user ? loggedInNav : publicNav;

  // Overlays bei jedem Routenwechsel schließen, damit kein Zombie-Drawer stehenbleibt.
  useEffect(() => { setMenuOpen(false); setAdminSheetOpen(false); }, [location.pathname]);

  // Body-Scroll sperren, solange ein Overlay offen ist.
  useEffect(() => {
    const anyOpen = menuOpen || adminSheetOpen;
    if (!anyOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen, adminSheetOpen]);

  const closeInstall = () => {
    try { localStorage.setItem("wbc_install_prompt_dismissed_v2", "true"); } catch { /* ignore */ }
    setInstallOpen(false);
  };

  const signOut = async () => { await logout(); setMenuOpen(false); navigate("/login"); };

  return <>
    <header className="mobile-topbar" data-testid="mobile-topbar">
      <button type="button" className="mobile-icon-button" onClick={() => setMenuOpen(true)} data-testid="mobile-burger-button" aria-label="Menü öffnen"><Menu size={22} /></button>
      <div className="mobile-brand-row"><img src={LOGO_URL} alt="WBC Germany" data-testid="mobile-header-logo" /><span>WBC MUAY THAI</span></div>
    </header>

    {menuOpen && <>
      <div className="mobile-drawer-backdrop" onClick={() => setMenuOpen(false)} data-testid="mobile-drawer-backdrop" />
      <aside className="mobile-drawer" data-testid="mobile-drawer" role="dialog" aria-label="Hauptmenü">
        <header className="mobile-drawer-header">
          <div className="mobile-brand-row"><img src={LOGO_URL} alt="WBC Germany" /><span>WBC MUAY THAI</span></div>
          <button type="button" className="mobile-icon-button" onClick={() => setMenuOpen(false)} data-testid="mobile-drawer-close" aria-label="Menü schließen"><X size={22} /></button>
        </header>
        <nav className="mobile-drawer-nav" data-testid="mobile-drawer-nav">
          {navItems.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `mobile-drawer-link ${isActive ? "active" : ""}`} onClick={() => setMenuOpen(false)} data-testid={`mobile-drawer-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-link`}><Icon size={20} /><span>{label}</span></NavLink>)}
        </nav>
        <footer className="mobile-drawer-footer">
          {user && <div className="user-chip" data-testid="mobile-drawer-user"><span className="role-dot" /><div><b>{user?.full_name}</b><small>{user?.role?.replace("_", " ")}</small></div></div>}
          <button type="button" className="mobile-drawer-link" onClick={() => { setInstallOpen(true); setMenuOpen(false); }} data-testid="mobile-drawer-install"><Download size={20} /><span>App speichern</span></button>
          {user
            ? <button type="button" className="mobile-drawer-link danger" onClick={signOut} data-testid="mobile-drawer-logout"><LogOut size={20} /><span>Abmelden</span></button>
            : <>
                <Link to="/login" className="mobile-drawer-link" onClick={() => setMenuOpen(false)} data-testid="mobile-drawer-login"><LogIn size={20} /><span>Anmelden</span></Link>
                <Link to="/register" className="mobile-drawer-link" onClick={() => setMenuOpen(false)} data-testid="mobile-drawer-register"><UserPlus size={20} /><span>Registrieren</span></Link>
              </>}
        </footer>
      </aside>
    </>}

    {canManageEvents && <nav className="admin-bottom-bar" data-testid="admin-bottom-bar">
      <button type="button" className="admin-bottom-trigger" onClick={() => setAdminSheetOpen(true)} data-testid="admin-bottom-open">
        <Shield size={20} />
        <span><b>ADMIN-PFLEGE</b><small>{visibleAdminItems.length} Bereiche</small></span>
        <ChevronRight size={18} style={{ transform: "rotate(-90deg)" }} />
      </button>
    </nav>}

    {adminSheetOpen && <>
      <div className="admin-sheet-backdrop" onClick={() => setAdminSheetOpen(false)} data-testid="admin-sheet-backdrop" />
      <aside className="admin-sheet" data-testid="admin-sheet" role="dialog" aria-label="Admin-Pflegebereiche">
        <header className="admin-sheet-header">
          <b><Shield size={18} /> Admin-Pflegebereiche</b>
          <button type="button" className="mobile-icon-button" onClick={() => setAdminSheetOpen(false)} data-testid="admin-sheet-close" aria-label="Schließen"><X size={22} /></button>
        </header>
        <nav className="admin-sheet-list" data-testid="admin-sheet-list">
          {visibleAdminItems.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} className={({ isActive }) => `mobile-drawer-link ${isActive ? "active" : ""}`} onClick={() => setAdminSheetOpen(false)} data-testid={`admin-sheet-${to.replace(/[^a-z]/gi, "-")}-link`}><Icon size={20} /><span>{label}</span></NavLink>)}
        </nav>
      </aside>
    </>}

    <InstallModal open={installOpen} onClose={closeInstall} deferredPrompt={null} />
  </>;
}
